import React, {
  ChangeEvent,
  Dispatch,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { debounce } from "debounce";
import { useAppState, useSelectedSession } from "../../state";
import { Action } from "../../state";
import { TaskType } from "../Task/Types";

interface AtomInputAddTaskProps {
  newTask: TaskType;
  setNewTask: Dispatch<TaskType>;
  placeholder?: string;
}
export const AtomInputAddTask = ({
  newTask,
  setNewTask,
  placeholder,
}: AtomInputAddTaskProps) => {
  const [state, dispatch] = useAppState();
  const session = useSelectedSession();
  const [suggestion, setSuggestion] = React.useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleBlur = () => {
    setSuggestion("");
    if (newTask.name !== "") {
      createTask(newTask);
    }
  };
  const handleClickOutside = () => {
    setSuggestion("");
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const dispatchAction = (text: string): Action => {
    return {
      type: "SET_TASK_NAME",
      payload: {
        sessionId: session.id,
        taskId: newTask.id,
        name: text,
      },
    };
  };

  const createTask = (newTask: TaskType) => {
    let task = {
      id: Math.floor(Math.random() * 1000),
      name: newTask.name,
      minutes: newTask.minutes,
      active: false,
    };
    dispatch({
      type: "CREATE_TASK",
      payload: { sessionId: state.currentSession, task: task },
    });
    setNewTask({
      id: -1,
      name: "",
      description: "",
      minutes: 0,
      active: false,
    });
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" && suggestion !== "") {
      e.preventDefault();
      dispatch(dispatchAction(suggestion));
      newTask.name = suggestion;
      setNewTask(newTask);
      // clears suggestion
      setSuggestion("");
    }
    if (e.key === "Enter") {
      if (newTask.name === "") {
        throw Error("Task name cannot be empty");
      }
      createTask(newTask);
    }
    if (e.key === "Escape") {
      setSuggestion("");
    }
  };

  const autocomplete = async (input: any) => {
    const response = await fetch("/.netlify/functions/autocomplete", {
      method: "POST",
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    if ("message" in data) {
      return data.message;
    }
  };

  useEffect(() => {
    setSuggestion("");
  }, [newTask.name]);

  // useMemo beccause debounce is stateful
  const handleNameChange = useMemo(() => {
    // debounce - Useful for implementing behavior that should only happen after a repeated action has completed.
    const debounceAutocomplete = debounce(
      async (e: ChangeEvent<HTMLInputElement>) => {
        const response: any = await autocomplete({ text: e.target.value });
        setSuggestion(response.replace(/"/g, ""));
      },
      200
    );

    return async (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(dispatchAction(e.target.value));
      newTask.name = e.target.value;
      setNewTask(newTask);

      const words = e.target.value.trim().split(/\s+/);
      if (words.length >= 1) {
        debounceAutocomplete(e);
      } else {
        setSuggestion("");
      }
    };
  }, [newTask, dispatch, session, setSuggestion]);

  return (
    <div className="relative bg-transparent">
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder || "Add Task"}
        value={newTask.name}
        onChange={handleNameChange}
        className="bg-inherit border-none w-full focus:outline-none"
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      ></input>
      {suggestion !== "" && (
        <div
          className="absolute top-full left-0 mt-1 py-1 px-2 text-sm bg-white text-gray-600 border border-gray-200 rounded shadow-lg"
          style={{
            minWidth: inputRef.current?.offsetWidth || "100%",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {suggestion}
        </div>
      )}
    </div>
  );
};
